/* ═══════════════════════════════════════════════════════════════════════════
   Chase N Chew — collection filter drawer
   The web twin of applyFilters() + the Sort / Filters sheets in
   src/screens/ShopScreen.js.

   Why client-side: the app's groups are derived facets, not Shopify facets.
   "Life Stage" and "Breed Size" come from keywords in the title/tags/body,
   "Discount Range" from the compare-at spread, and "Pet" from which toy
   collection a product is NOT in. Shopify's storefront filters can't express
   any of those, so cnc-product-card bakes each product's facets into
   data-cnc-* attributes and this file filters the rendered grid.

   State lives in the URL (cnc_f / cnc_s) so a filtered view is shareable and
   survives back/forward. Shopify's own filter params are left untouched.
   ═══════════════════════════════════════════════════════════════════════════ */

(function () {
  "use strict";

  /* `flag` (New Arrivals / Sale) is a group like any other as far as state,
     counting and Clear are concerned. It just has no dropdown chip — the rail
     surfaces it as two toggles, and the drawer still lists it under
     Highlights so the drawer is never a partial view of what is filtering. */
  /* `type` is the sub-collection facet, and the only group whose options are
     not fixed: the section works out which sub-collections are actually
     represented among the rendered products and prints only those. It exists
     on merchandising collections — a price band, Sale, New Toys — where the
     products span every type and the useful control is "narrow this list".
     On a real category page (Dog Toys and its children) there is no facet at
     all: the section renders the types as LINKS there instead, because the
     siblings' products are not on the page to be filtered. */
  var GROUP_KEYS = ["type", "pet", "stage", "size", "price", "discount", "flag"];

  /* PRICE_RANGES in ShopScreen.js — values are rupees, matching data-cnc-price. */
  var PRICE = {
    under200: [0, 200],
    "201-500": [201, 500],
    "501-1000": [501, 1000],
    above1000: [1001, Infinity]
  };

  var FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  function emptyState() {
    return {
      type: [],
      pet: [],
      stage: [],
      size: [],
      price: [],
      discount: [],
      flag: []
    };
  }

  function tokens(el, name) {
    var v = el.getAttribute(name);
    return v ? v.trim().split(/\s+/) : [];
  }

  /* ── URL <-> state ─────────────────────────────────────────────────────── */

  function readUrl() {
    var p = new URLSearchParams(window.location.search);
    var f = emptyState();
    var raw = p.get("cnc_f");
    if (raw) {
      raw.split(";").forEach(function (part) {
        var i = part.indexOf(":");
        if (i < 0) return;
        var k = part.slice(0, i);
        if (GROUP_KEYS.indexOf(k) < 0) return;
        var vals = part.slice(i + 1);
        f[k] = vals ? vals.split(",").filter(Boolean) : [];
      });
    }
    return { f: f, sort: p.get("cnc_s") || "popular" };
  }

  function writeUrl(f, sort) {
    var p = new URLSearchParams(window.location.search);
    var parts = GROUP_KEYS.filter(function (k) {
      return f[k].length;
    }).map(function (k) {
      return k + ":" + f[k].join(",");
    });

    if (parts.length) p.set("cnc_f", parts.join(";"));
    else p.delete("cnc_f");

    if (sort && sort !== "popular") p.set("cnc_s", sort);
    else p.delete("cnc_s");

    var qs = p.toString();
    window.history.replaceState(
      null,
      "",
      window.location.pathname + (qs ? "?" + qs : "") + window.location.hash
    );
  }

  /* ── The filter itself — a line-for-line port of applyFilters() ────────── */

  function matches(card, f) {
    /* Type: straight membership of a sub-collection, read off the same
       data-cnc-cols the Pet rule below uses — the card already carries every
       collection handle its product belongs to, so this costs nothing new.
       OR within the group, like every other group here. */
    if (f.type.length) {
      var tcols = tokens(card, "data-cnc-cols");
      var typeOk = f.type.some(function (handle) {
        return tcols.indexOf(handle) > -1;
      });
      if (!typeOk) return false;
    }

    /* Pet: the app asks which toy collection the product is NOT in, so a
       product that lives in neither dog-toys nor cat-toys passes both. */
    if (f.pet.length) {
      var cols = tokens(card, "data-cnc-cols");
      var petOk = f.pet.some(function (pet) {
        return pet === "Dog"
          ? cols.indexOf("cat-toys") < 0
          : cols.indexOf("dog-toys") < 0;
      });
      if (!petOk) return false;
    }

    if (f.stage.length) {
      var stage = tokens(card, "data-cnc-stage");
      if (
        !f.stage.some(function (s) {
          return stage.indexOf(s) > -1;
        })
      )
        return false;
    }

    if (f.size.length) {
      var size = tokens(card, "data-cnc-size");
      if (
        !f.size.some(function (s) {
          return size.indexOf(s) > -1;
        })
      )
        return false;
    }

    if (f.price.length) {
      var v = parseFloat(card.getAttribute("data-cnc-price") || "0");
      if (
        !f.price.some(function (r) {
          var range = PRICE[r];
          return range && v >= range[0] && v <= range[1];
        })
      )
        return false;
    }

    if (f.discount.length) {
      var d = parseInt(card.getAttribute("data-cnc-disc") || "0", 10);
      if (
        !f.discount.some(function (x) {
          return d >= Number(x);
        })
      )
        return false;
    }

    if (f.flag.length) {
      var flags = tokens(card, "data-cnc-flags");
      if (
        !f.flag.some(function (x) {
          return flags.indexOf(x) > -1;
        })
      )
        return false;
    }

    return true;
  }

  var SORTERS = {
    popular: function (a, b) {
      return a._i - b._i;
    },
    az: function (a, b) {
      return a._t.localeCompare(b._t);
    },
    za: function (a, b) {
      return b._t.localeCompare(a._t);
    },
    priceLow: function (a, b) {
      return a._p - b._p;
    },
    priceHigh: function (a, b) {
      return b._p - a._p;
    }
  };

  var SORT_SHORT = {
    az: "A–Z",
    za: "Z–A",
    priceLow: "Price ↓",
    priceHigh: "Price ↑"
  };

  /* ── Wire one collection section ───────────────────────────────────────── */

  function init(root) {
    /* What the grid is full of. Collections count products; search counts
       results, and "18 of 43 products" on a search page is the kind of small
       wrong word that makes a page feel borrowed. Both nouns come off the
       root so the rest of this file never has to know which page it is on. */
    var NOUN = root.dataset.cncNoun || "product";
    var NOUNS = root.dataset.cncNounPlural || NOUN + "s";
    function noun(k) { return k === 1 ? NOUN : NOUNS; }

    if (root.__cncFilters) return;
    root.__cncFilters = true;

    var drawer = root.querySelector("[data-cnc-drawer]");
    var grid = root.querySelector("[data-cnc-grid]");
    if (!drawer || !grid) return;

    var panel = drawer.querySelector(".cnc-fd__panel");
    var scrim = drawer.querySelector("[data-cnc-scrim]");
    // Every count on the page: the toolbar's (desktop) and the one beside
    // the sticky title (phones).
    var countEls = Array.prototype.slice.call(root.querySelectorAll("[data-cnc-count]"));
    var countEl = countEls[0] || null;
    var emptyEl = root.querySelector("[data-cnc-empty]");
    var applyBtn = drawer.querySelector("[data-cnc-apply]");
    var clearBtn = drawer.querySelector("[data-cnc-clear]");
    var closeBtn = drawer.querySelector("[data-cnc-close]");
    var titleEl = drawer.querySelector("[data-cnc-title]");
    var noteEl = root.querySelector("[data-cnc-note]");
    var boxes = Array.prototype.slice.call(
      drawer.querySelectorAll("input[data-cnc-group]")
    );
    var radios = Array.prototype.slice.call(
      drawer.querySelectorAll('input[name="cnc-sort"]')
    );
    var chips = Array.prototype.slice.call(
      root.querySelectorAll("[data-cnc-open]")
    );
    var clearChip = root.querySelector("[data-cnc-clearall]");
    var toggles = Array.prototype.slice.call(
      root.querySelectorAll("[data-cnc-toggle]")
    );

    /* Cache each card's sort keys once — the grid never re-renders. */
    var cards = Array.prototype.slice.call(grid.children).map(function (el, i) {
      el._i = i;
      /* data-cnc-name, not data-cnc-title: the drawer heading owns
         [data-cnc-title], and one attribute must not mean two things. */
      el._t = el.getAttribute("data-cnc-name") || "";
      el._p = parseFloat(el.getAttribute("data-cnc-price") || "0");
      return el;
    });

    var state = readUrl();
    var lastFocus = null;

    /* ── Options nobody on this page can use ─────────────────────────────
       The filter list is the same fixed set on every collection (it mirrors
       the app's FILTER_GROUPS), so Dog Toys offered "Cat" and "Kitten" —
       choices that can only ever empty the grid — and "Dog", which can only
       ever match everything. Each option is tried alone against the cards
       actually on the page: one that matches none, or matches every card,
       narrows nothing and is hidden. A group left with no options goes, and
       so does its chip in the rail. An option already ticked (from the URL)
       is never hidden — the customer has to be able to see and untick it.
       Type is left alone; the sub-collection nav owns it and prints counts. */
    (function pruneOptions() {
      if (!cards.length) return;
      var ticked = {};
      GROUP_KEYS.forEach(function (k) {
        (state.f[k] || []).forEach(function (v) { ticked[k + ":" + v] = true; });
      });
      boxes.forEach(function (box) {
        var key = box.getAttribute("data-cnc-group");
        if (key === "type" || ticked[key + ":" + box.value]) return;
        var f = emptyState();
        f[key] = [box.value];
        var hits = cards.filter(function (c) { return matches(c, f); }).length;
        var useless = hits === 0 || hits === cards.length;
        var opt = box.closest(".cnc-fopt") || box.parentNode;
        if (opt) opt.hidden = useless;
      });
      drawer.querySelectorAll(".cnc-fgroup[data-cnc-section]").forEach(function (g) {
        var key = g.getAttribute("data-cnc-section");
        if (key === "type") return;
        var left = Array.prototype.some.call(g.querySelectorAll(".cnc-fopt"), function (o) { return !o.hidden; });
        g.hidden = !left;
        root.querySelectorAll('[data-cnc-open="' + key + '"], [data-cnc-toggle="' + key + '"]').forEach(function (chip) {
          chip.hidden = !left;
        });
      });
      // Rail toggles are single values of the "flag" group (New / Sale).
      root.querySelectorAll("[data-cnc-toggle]").forEach(function (t) {
        var v = t.getAttribute("data-cnc-toggle");
        if (ticked["flag:" + v]) return;
        var f = emptyState(); f.flag = [v];
        var hits = cards.filter(function (c) { return matches(c, f); }).length;
        t.hidden = hits === 0 || hits === cards.length;
      });
    })();

    /* ── Render ─────────────────────────────────────────────────────────── */

    function render(pushUrl) {
      var f = state.f;
      var visible = [];

      cards.forEach(function (card) {
        if (matches(card, f)) {
          card.removeAttribute("data-cnc-hidden");
          visible.push(card);
        } else {
          card.setAttribute("data-cnc-hidden", "");
        }
      });

      /* Sort by reordering grid tracks, not the DOM — no layout thrash and
         the cards keep their event listeners and image decode state. */
      visible.slice().sort(SORTERS[state.sort] || SORTERS.popular)
        .forEach(function (card, i) {
          card.style.order = String(i + 1);
        });

      var n = visible.length;
      var total = cards.length;
      var active = GROUP_KEYS.reduce(function (sum, k) {
        return sum + f[k].length;
      }, 0);

      if (countEl) {
        var countTxt = active
          ? n + " of " + total + " " + noun(total)
          : total + " " + noun(total);
        countEls.forEach(function (el) { el.textContent = countTxt; });
      }

      if (emptyEl) emptyEl.classList.toggle("is-on", n === 0);
      grid.style.display = n === 0 ? "none" : "";
      root.classList.toggle("cnc-fd-filtering", active > 0);

      /* Rail chips */
      chips.forEach(function (chip) {
        var key = chip.getAttribute("data-cnc-open");
        var nEl = chip.querySelector("[data-cnc-chipn]");
        var lbl = chip.querySelector("[data-cnc-chiplabel]");

        if (key === "sort") {
          var on = state.sort !== "popular";
          chip.classList.toggle("is-on", on);
          if (lbl) lbl.textContent = on ? SORT_SHORT[state.sort] : "Sort";
          return;
        }
        if (key === "filters") {
          chip.classList.toggle("is-on", active > 0);
          if (nEl) {
            nEl.textContent = active;
            nEl.hidden = active === 0;
          }
          return;
        }
        var c = (f[key] || []).length;
        chip.classList.toggle("is-on", c > 0);
        if (lbl) {
          lbl.textContent =
            chip.getAttribute("data-cnc-chiptitle") + (c ? " · " + c : "");
        }
      });

      /* Rail toggles — same state, different affordance. */
      toggles.forEach(function (t) {
        var on = f.flag.indexOf(t.getAttribute("data-cnc-toggle")) > -1;
        t.classList.toggle("is-on", on);
        t.setAttribute("aria-pressed", on ? "true" : "false");
      });

      if (clearChip) clearChip.hidden = active === 0;
      if (clearBtn) clearBtn.disabled = active === 0;
      if (noteEl) noteEl.hidden = active === 0;

      /* Per-group counts inside the drawer, e.g. "PET · 2". */
      GROUP_KEYS.forEach(function (k) {
        var lab = drawer.querySelector('[data-cnc-glabel="' + k + '"]');
        if (!lab) return;
        var c = f[k].length;
        lab.innerHTML =
          lab.getAttribute("data-cnc-gtitle") + (c ? " <b>· " + c + "</b>" : "");
      });

      if (titleEl && drawer.getAttribute("data-mode") !== "sort") {
        titleEl.innerHTML =
          "Filters" + (active ? ' <span>· ' + active + "</span>" : "");
      }

      if (applyBtn) {
        applyBtn.textContent =
          n === 0
            ? "No " + NOUNS + " match"
            : "Show " + n + " " + noun(n);
        applyBtn.classList.toggle("is-void", n === 0);
      }

      /* Keep the controls honest if state arrived from the URL. */
      boxes.forEach(function (box) {
        var k = box.getAttribute("data-cnc-group");
        box.checked = f[k].indexOf(box.value) > -1;
      });
      radios.forEach(function (r) {
        r.checked = r.value === state.sort;
      });

      if (pushUrl !== false) writeUrl(f, state.sort);
    }

    /* ── Drawer open / close ────────────────────────────────────────────── */

    function open(mode, groupKey) {
      lastFocus = document.activeElement;
      drawer.setAttribute("data-mode", mode);
      if (titleEl && mode === "sort") titleEl.textContent = "Sort by";
      else render(false);
      drawer.classList.add("is-open");
      drawer.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("cnc-fd-lock");

      chips.forEach(function (c) {
        var k = c.getAttribute("data-cnc-open");
        c.setAttribute(
          "aria-expanded",
          k === mode || (mode === "filters" && k === groupKey) ? "true" : "false"
        );
      });

      window.setTimeout(function () {
        if (groupKey) {
          var sec = drawer.querySelector('[data-cnc-section="' + groupKey + '"]');
          if (sec) {
            var body = drawer.querySelector(".cnc-fd__body");
            if (body) body.scrollTop = Math.max(sec.offsetTop - 8, 0);
          }
        }
        (closeBtn || panel).focus();
      }, 60);
    }

    function close() {
      drawer.classList.remove("is-open");
      drawer.setAttribute("aria-hidden", "true");
      document.documentElement.classList.remove("cnc-fd-lock");
      chips.forEach(function (c) {
        c.setAttribute("aria-expanded", "false");
      });
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    }

    /* ── Events ─────────────────────────────────────────────────────────── */

    chips.forEach(function (chip) {
      chip.addEventListener("click", function () {
        var key = chip.getAttribute("data-cnc-open");
        if (key === "sort") open("sort");
        else if (key === "filters") open("filters");
        else open("filters", key);
      });
    });

    toggles.forEach(function (t) {
      t.addEventListener("click", function () {
        var v = t.getAttribute("data-cnc-toggle");
        var arr = state.f.flag;
        var i = arr.indexOf(v);
        if (i > -1) arr.splice(i, 1);
        else arr.push(v);
        render();
      });
    });

    boxes.forEach(function (box) {
      box.addEventListener("change", function () {
        var k = box.getAttribute("data-cnc-group");
        var arr = state.f[k];
        var i = arr.indexOf(box.value);
        if (box.checked && i < 0) arr.push(box.value);
        else if (!box.checked && i > -1) arr.splice(i, 1);
        render();
      });
    });

    radios.forEach(function (r) {
      r.addEventListener("change", function () {
        if (!r.checked) return;
        state.sort = r.value;
        render();
        window.setTimeout(close, 180);
      });
    });

    if (clearBtn)
      clearBtn.addEventListener("click", function () {
        state.f = emptyState();
        render();
      });

    if (clearChip)
      clearChip.addEventListener("click", function () {
        state.f = emptyState();
        render();
      });

    if (emptyEl) {
      var emptyClear = emptyEl.querySelector("[data-cnc-emptyclear]");
      if (emptyClear)
        emptyClear.addEventListener("click", function () {
          state.f = emptyState();
          render();
        });
    }

    if (applyBtn) applyBtn.addEventListener("click", close);
    if (closeBtn) closeBtn.addEventListener("click", close);
    if (scrim) scrim.addEventListener("click", close);

    drawer.addEventListener("keydown", function (e) {
      if (e.key === "Escape") {
        e.stopPropagation();
        close();
        return;
      }
      if (e.key !== "Tab") return;

      /* Focus trap — the drawer is modal, so Tab must not reach the page. */
      var items = Array.prototype.slice
        .call(panel.querySelectorAll(FOCUSABLE))
        .filter(function (el) {
          return el.offsetParent !== null;
        });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    /* The cart drawer sits above this one — never show both. */
    document.addEventListener("click", function (e) {
      if (!drawer.classList.contains("is-open")) return;
      var t = e.target.closest && e.target.closest("[data-cart-drawer-open], .cart-icon, [href='/cart']");
      if (t) close();
    });

    render(false);
  }

  /* ── Sticky rail ────────────────────────────────────────────────────────
     The rail sticks beneath the header rather than at the viewport top, so it
     needs the header's live height — which varies with breakpoint and with any
     announcement bar the merchant adds above it. Measure, publish as
     --cnc-hd-h, and flag the merged state so the header can drop its shadow
     and let the pair read as a single bar. */

  function wireStickyRail() {
    var rail = document.querySelector(".cnc-frail");
    if (!rail) return;
    var header = document.querySelector(".cnc-hd");
    var root = document.documentElement;

    function syncOffset() {
      var h = header ? Math.round(header.getBoundingClientRect().height) : 0;
      root.style.setProperty("--cnc-hd-h", h + "px");
    }

    function syncStuck() {
      var h = header ? header.getBoundingClientRect().height : 0;
      root.classList.toggle(
        "cnc-rail-stuck",
        rail.getBoundingClientRect().top <= h + 1
      );
    }

    syncOffset();
    syncStuck();

    window.addEventListener("scroll", syncStuck, { passive: true });
    window.addEventListener(
      "resize",
      function () {
        syncOffset();
        syncStuck();
      },
      { passive: true }
    );

    /* The header grows and shrinks on its own (search focus, sticky restyle),
       and resize alone would miss that. */
    if (header && window.ResizeObserver) {
      new window.ResizeObserver(function () {
        syncOffset();
        syncStuck();
      }).observe(header);
    }
  }

  function boot() {
    document.querySelectorAll("[data-cnc-filters]").forEach(init);
    wireStickyRail();
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot);
  else boot();

  /* Theme editor: re-wire when the section is re-rendered. */
  document.addEventListener("shopify:section:load", boot);
})();
